import Checkbox from '@ben/orion/checkbox';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import FileViewList from '@ben/orion/file-view-list';
import Icon, { IconName, IconScale } from '@ben/orion/icon';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Tooltip from '@ben/orion/tooltip';
import type { EmployerBenefitDocument } from '@ben/shared/types/benefits';
import type { ChangeEvent } from 'react';
import { memo, useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

interface IDocumentUploadFormProps {
  document: EmployerBenefitDocument;
  onDelete: () => void;
  onSave: (documentUploadForm: EmployerBenefitDocument) => void;
  editing?: boolean;
  isInitialUpload?: boolean;
}

const TITLE_MAX_LENGTH = 50;

const DocumentUploadForm = ({
  document,
  onDelete,
  onSave,
  editing = false,
  isInitialUpload = false,
}: IDocumentUploadFormProps) => {
  const theme = useTheme();
  const [documentUploadForm, setDocumentUploadForm] =
    useState<EmployerBenefitDocument>({
      name: document.name ?? '',
      description: document.description ?? '',
      file: document.file,
      isPolicyDocument: document.isPolicyDocument ?? false,
    });

  const isFileNameError = documentUploadForm?.name?.length > TITLE_MAX_LENGTH;

  const handleFormValueChange = useCallback(
    (name: string, event: ChangeEvent<HTMLInputElement>) => {
      setDocumentUploadForm({
        ...documentUploadForm,
        [name]: event.target.value,
      });
    },
    [documentUploadForm]
  );

  const handlePolicyDocumentChange = useCallback(
    (value: boolean) => {
      setDocumentUploadForm({
        ...documentUploadForm,
        isPolicyDocument: value,
      });
    },
    [documentUploadForm]
  );

  const handleSave = useCallback(() => {
    onSave(documentUploadForm);
  }, [documentUploadForm, onSave]);

  return (
    <Container>
      <Input
        variant={InputVariant.Text}
        name={'File name'}
        placeholder={'Example file name'}
        value={documentUploadForm.name}
        width="100%"
        onChange={(event) => {
          handleFormValueChange('name', event);
        }}
        maxLength={TITLE_MAX_LENGTH}
        hasError={isFileNameError}
        errorMessage={'File name cannot exceed 50 characters'}
      />
      <Input
        variant={InputVariant.TextArea}
        name={'Description'}
        placeholder={'Example description'}
        width="100%"
        value={documentUploadForm.description}
        onChange={(event) => {
          handleFormValueChange('description', event);
        }}
      />
      <RowWrapper>
        <CheckboxWrapper>
          <Checkbox
            label="Is policy document"
            showLabel
            value={documentUploadForm.isPolicyDocument ?? false}
            onChange={(value: boolean) => {
              handlePolicyDocumentChange(value);
            }}
          />
        </CheckboxWrapper>
        <Tooltip
          tooltipText={
            'A policy document is only displayed in the ECC. It will not be visible to employees.'
          }
        >
          <Icon
            name={IconName.Info}
            color={theme.palette.neutral[80]}
            scale={IconScale.S}
          />
        </Tooltip>
      </RowWrapper>
      {!editing && (
        <DocumentWrapper>
          <FileViewList
            documents={[
              {
                file: documentUploadForm.file || '',
                name: documentUploadForm.name,
                description: documentUploadForm.description,
              },
            ]}
          />
          <StyledInlineTextButton onClick={onDelete} label={'Remove'} />
        </DocumentWrapper>
      )}
      <StyledButton
        variant={ButtonVariant.primary}
        label={
          isInitialUpload && !editing ? 'Attach document' : 'Save document'
        }
        onClick={handleSave}
        width="fit-content"
        disabled={isFileNameError}
      />
    </Container>
  );
};

export default memo(DocumentUploadForm);

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    width: 100%;
  `}
`;

const DocumentWrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${theme.spacing(3)};
  `}
`;

const RowWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CheckboxWrapper = styled.div`
  ${({ theme }) => css`
    margin-right: ${theme.spacing(1)};
  `}
`;

const StyledButton = styled(Button)`
  margin-left: auto;
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    ${theme.typography.bodyMd2}
  `}
`;
